import { AMFAST_STATUSES, buildAmFastStatusBody, checkAmFastUpload, shipmentStatusText, validateAmFastStatus, type AmFastStatus, type AmFastStatusDraft } from "@flexfreight/core";
import {
  Banner,
  Button,
  DataTable,
  DatePicker,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FileField,
  FilterToolbar,
  notify,
  PageHeader,
  SearchInput,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TextAreaField,
  TextField,
  TimeField,
  Truncate,
  pageRows,
  sortRows,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { BackLink, CustomerLink, day, documentHref, label, message, sortOf, sortPatch, time, useCan, when } from "./ops";

export type AmFastRow = { orgId: string; orgName: string; shipmentId: string; bol: string | null; pro: string | null; status: string; pickupDate: string | null; estimatedDelivery: string | null; origin: string; destination: string; updatedAt: string };
type AmFastRecord = {
  shipmentId: string;
  bol: string;
  pro?: string;
  pickupConfirmation?: string;
  status: string;
  estimatedDelivery?: string;
  deliveredAt?: string;
  signedBy?: string;
  events: { at: string; status: string; description: string; location?: { city: string; region: string }; exceptionCode?: string }[];
  documents: { id: string; kind: string; contentType: string; sizeBytes: number; createdAt: string }[];
};

/** "2026-10-14T09:30" in local time (what the status API takes for `at`). */
const localNow = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};
const emptyDraft = (status: AmFastStatus): AmFastStatusDraft => ({ status, at: localNow(), description: "", city: "", region: "", exceptionCode: "", pro: "", pickupConfirmation: "", estimatedDelivery: "", signedBy: "" });
/** The next step ops usually records after the current status. */
const NEXT: Record<string, AmFastStatus> = { booked: "dispatched", dispatched: "picked_up", picked_up: "in_transit", in_transit: "out_for_delivery", out_for_delivery: "delivered", exception: "in_transit" };
const FILTERS = { status: "", q: "", sort: "", dir: "asc", page: 0 };
const PAGE_SIZE = 50;
const docName = (kind: string) => (kind === "pod" || kind === "bol" ? kind.toUpperCase() : label(kind));

/** Freight > AmFast tenders: AmFast loads are tendered by email; ops records what AmFast tells them. */
export function AmFastPage() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const navigate = useNavigate();
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const list = useQuery({ queryKey: ["console", "amfast"], queryFn: () => api<AmFastRow[]>("/v1/admin/amfast/shipments"), placeholderData: keepPreviousData, refetchInterval: 60_000 });
  const needle = v.q.toLowerCase();
  const sort = sortOf(v);
  const all = sortRows(
    (list.data ?? []).filter((r) => (!v.status || r.status === v.status) && (!needle || [r.bol, r.pro, r.orgName, r.origin, r.destination].some((x) => x?.toLowerCase().includes(needle)))),
    sort,
    (r, k) => (k === "bol" ? r.bol : k === "customer" ? r.orgName : k === "status" ? r.status : k === "pickup" ? time(r.pickupDate) : k === "eta" ? time(r.estimatedDelivery) : k === "updated" ? time(r.updatedAt) : null),
  );
  const rows = pageRows(all, v.page, PAGE_SIZE);
  const href = (r: AmFastRow) => `/amfast/${r.orgId}/${r.shipmentId}`;
  const items: FilterItem[] = [
    {
      key: "status",
      priority: 1,
      active: v.status !== "",
      inline: <Select variant="toolbar" label="Status" value={v.status} onChange={(status) => f.set({ status })} options={[{ value: "", label: "Any open" }, ...AMFAST_STATUSES.filter((s) => s !== "delivered" && s !== "cancelled").map((s) => ({ value: s, label: shipmentStatusText(s) }))]} />,
    },
  ];
  const columns: Column<AmFastRow>[] = [
    {
      key: "bol",
      header: "Shipment",
      primary: true,
      sortable: true,
      width: "11rem",
      render: (r) => (
        <>
          <Link className="ffa-rowlink ffa-cell-strong" to={href(r)}>{r.bol ?? "No BOL"}</Link>
          <span className="ffa-block ffa-muted">{r.pro ? `PRO ${r.pro}` : "No PRO yet"}</span>
        </>
      ),
    },
    { key: "customer", header: "Customer", sortable: true, truncate: "custom", render: (r) => <Truncate text={r.orgName}><CustomerLink orgId={r.orgId} name={r.orgName} /></Truncate> },
    { key: "lane", header: "Lane", render: (r) => `${r.origin} → ${r.destination}` },
    { key: "status", header: "Status", sortable: true, width: "9.5rem", render: (r) => <StatusBadge domain="shipment" value={r.status} /> },
    { key: "pickup", header: "Pickup", sortable: true, width: "8rem", render: (r) => day(r.pickupDate) },
    { key: "eta", header: "ETA", sortable: true, width: "8rem", mobile: "hide", render: (r) => day(r.estimatedDelivery) },
    { key: "updated", header: "Last update", sortable: true, sortFirst: "desc", width: "9.5rem", mobile: "hide", render: (r) => when(r.updatedAt) },
  ];
  return (
    <section className="ffa-page">
      <PageHeader title="AmFast tenders" subtitle="AmFast loads are tendered by email at booking. Record what AmFast tells you (PRO, pickup, transit, delivery) so customers see it. Oldest pickup first." />
      <FilterToolbar
        label="AmFast filters"
        search={<SearchInput label="Search BOL, PRO, customer or city" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
        meta={list.data ? `${all.length} open` : undefined}
      />
      <DataTable
        caption="Open AmFast shipments"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.shipmentId}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load AmFast shipments", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive ? "No AmFast shipments match." : "No open AmFast shipments."}
        rowTone={(r) => (r.status === "exception" ? "danger" : null)}
        sort={sort}
        onSortChange={(s) => f.set(sortPatch(s))}
        onRowClick={(r) => navigate(href(r))}
        rowHref={href}
        pagination={all.length > PAGE_SIZE ? { page: v.page, pageSize: PAGE_SIZE, total: all.length, onPageChange: (page) => f.set({ page }) } : undefined}
      />
    </section>
  );
}

type Doc = AmFastRecord["documents"][number];

/** Freight > AmFast tenders > one load: DetailLayout with its timeline and documents, and the status form beside them. */
export function AmFastShipmentPage() {
  const { orgId = "", shipmentId = "" } = useParams();
  const can = useCan();
  const key = ["console", "amfast", orgId, shipmentId];
  const q = useQuery({ queryKey: key, queryFn: () => api<AmFastRecord>(`/v1/admin/orgs/${orgId}/amfast/shipments/${shipmentId}`) });
  const back = <BackLink to="/amfast">AmFast tenders</BackLink>;
  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout header={<PageHeader back={back} eyebrow="AmFast load" title={<Skeleton width="12rem" height="2.25rem" />} subtitle={<Skeleton width="20rem" />} />} aside={<Section title="Record what AmFast told you"><SkeletonText lines={6} /></Section>}>
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading AmFast load…</span>
      </section>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "AmFast load not found" : "AmFast load"} />
        {missing ? <Banner tone="warning" title="This AmFast shipment doesn't exist.">It may have been cancelled, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this AmFast load" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const r = q.data;
  const write = can("dispatch:write");
  const open = r.status !== "delivered" && r.status !== "cancelled";
  const docCols: Column<Doc>[] = [
    { key: "kind", header: "Document", primary: true, render: (d) => <a href={documentHref(r.shipmentId, d.id)} target="_blank" rel="noopener noreferrer">{docName(d.kind)}</a> },
    { key: "added", header: "Added", width: "9rem", render: (d) => day(d.createdAt) },
  ];
  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="AmFast load"
            title={r.bol}
            badges={<StatusBadge domain="shipment" value={r.status} />}
            subtitle={`AmFast${r.pro ? ` · PRO ${r.pro}` : " · no PRO yet"}${r.estimatedDelivery ? ` · ETA ${day(r.estimatedDelivery)}` : ""}`}
            primaryAction={<Link className="ffx-btn" to={`/shipments/${r.shipmentId}`}>Open shipment</Link>}
          />
        }
        aside={
          <>
            {write && open ? <StatusForm orgId={orgId} rec={r} queryKey={key} /> : null}
            <Section title="Load">
              <DefinitionList
                items={[
                  { term: "Status", detail: <StatusBadge domain="shipment" value={r.status} /> },
                  { term: "PRO", detail: r.pro ?? "No PRO yet" },
                  r.pickupConfirmation ? { term: "Pickup #", detail: r.pickupConfirmation } : null,
                  { term: "ETA", detail: day(r.estimatedDelivery) },
                  r.deliveredAt ? { term: "Delivered", detail: `${when(r.deliveredAt)}${r.signedBy ? `, signed by ${r.signedBy}` : ""}` } : null,
                ]}
              />
            </Section>
          </>
        }
        asideLabel="Record a status"
      >
        <Section title="Timeline" description="Newest first.">
          {r.events.length === 0 ? (
            <p className="ffa-muted">Nothing recorded yet.</p>
          ) : (
            <ol className="ffa-timeline">
              {[...r.events].reverse().map((e, i) => (
                <li key={`${e.at}-${i}`}>
                  <time dateTime={e.at} className="ffa-muted">{when(e.at)}</time>
                  <span>
                    <strong>{shipmentStatusText(e.status)}</strong> {e.description}
                    {e.location ? <span className="ffa-muted"> · {e.location.city}, {e.location.region}</span> : null}
                    {e.exceptionCode ? <span className="ffa-warn"> · {e.exceptionCode}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Section>
        <Section title="Documents" description={write && r.status !== "cancelled" ? "Attach the POD or BOL AmFast sends you; the customer sees it on their shipment." : undefined}>
          <DataTable caption="Documents" columns={docCols} rows={r.documents} rowKey={(d) => d.id} empty="No POD or BOL yet." maxHeight="none" density="compact" />
          {write && r.status !== "cancelled" ? <UploadForm orgId={orgId} shipmentId={shipmentId} queryKey={key} /> : null}
        </Section>
      </DetailLayout>
    </section>
  );
}

function StatusForm({ orgId, rec, queryKey }: { orgId: string; rec: AmFastRecord; queryKey: string[] }) {
  const qc = useQueryClient();
  const [d, setD] = useState<AmFastStatusDraft>(() => emptyDraft(NEXT[rec.status] ?? "in_transit"));
  const [errors, setErrors] = useState<Partial<Record<keyof AmFastStatusDraft, string>>>({});
  const set = (patch: Partial<AmFastStatusDraft>) => setD((x) => ({ ...x, ...patch }));
  const [atDate = "", atTime = ""] = d.at.split("T");
  const save = useMutation({
    mutationFn: () => api<AmFastRecord>(`/v1/admin/orgs/${orgId}/amfast/shipments/${rec.shipmentId}/status`, { method: "POST", body: buildAmFastStatusBody(d) }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ["console", "amfast"] });
      notify.success(`Recorded: ${shipmentStatusText(r.status)}`, "The customer sees it on their shipment. AmFast isn't contacted.");
      setD(emptyDraft(NEXT[r.status] ?? "in_transit"));
    },
    onError: (e) => notify.error("Not recorded", message(e)),
  });
  const submit = () => {
    const e = validateAmFastStatus(d);
    setErrors(e);
    if (Object.keys(e).length === 0) save.mutate();
  };
  return (
    <Section title="Record what AmFast told you">
      <Select label="Status" value={d.status} onChange={(x) => set({ status: x as AmFastStatus })} options={AMFAST_STATUSES.map((s) => ({ value: s, label: shipmentStatusText(s) }))} />
      <div className="ffx-fields">
        <DatePicker variant="field" rule="any" cutoffHour={null} label="Date" value={atDate || null} onChange={(iso) => set({ at: `${iso}T${atTime || "12:00"}` })} error={errors.at} hint={null} />
        <TimeField label="Time" value={atTime} step={15} onChange={(t) => set({ at: `${atDate}T${t}` })} />
      </div>
      <TextAreaField label="What happened" value={d.description} onChange={(e) => set({ description: e.target.value })} rows={2} maxLength={500} error={errors.description} />
      <div className="ffx-fields">
        <TextField label="City" value={d.city} onChange={(e) => set({ city: e.target.value })} error={errors.city} optional />
        <TextField label="State" value={d.region} onChange={(e) => set({ region: e.target.value })} error={errors.region} maxLength={3} optional />
      </div>
      {d.status === "exception" ? <TextField label="Exception code" value={d.exceptionCode} onChange={(e) => set({ exceptionCode: e.target.value.toUpperCase() })} error={errors.exceptionCode} hint="For example DAMAGE or REFUSED." /> : null}
      <TextField label="PRO number" value={d.pro} onChange={(e) => set({ pro: e.target.value })} error={errors.pro} optional={!!rec.pro} hint={rec.pro ? `Now ${rec.pro}.` : undefined} />
      {d.status === "dispatched" || d.status === "picked_up" ? <TextField label="Pickup confirmation" value={d.pickupConfirmation} onChange={(e) => set({ pickupConfirmation: e.target.value })} error={errors.pickupConfirmation} optional /> : null}
      <DatePicker variant="field" rule="any" cutoffHour={null} label="Estimated delivery" value={d.estimatedDelivery || null} onChange={(iso) => set({ estimatedDelivery: iso })} error={errors.estimatedDelivery} hint={null} optional />
      {d.status === "delivered" ? <TextField label="Signed by" value={d.signedBy} onChange={(e) => set({ signedBy: e.target.value })} error={errors.signedBy} /> : null}
      <div className="ffx-actions">
        <Button variant="primary" loading={save.isPending} onClick={submit}>Record status</Button>
      </div>
    </Section>
  );
}

function UploadForm({ orgId, shipmentId, queryKey }: { orgId: string; shipmentId: string; queryKey: string[] }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<"pod" | "bol">("pod");
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const up = useMutation({
    mutationFn: (fl: File) => api(`/v1/admin/orgs/${orgId}/amfast/shipments/${shipmentId}/documents?kind=${kind}`, { method: "POST", body: fl }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      notify.success(`${kind.toUpperCase()} attached`);
      setFile(null);
      if (input.current) input.current.value = "";
    },
    onError: (e) => notify.error("Not uploaded", message(e)),
  });
  return (
    <div className="ffa-form">
      <div className="ffx-fields">
        <Select label="Document" value={kind} onChange={(x) => setKind(x as "pod" | "bol")} options={[{ value: "pod", label: "Proof of delivery (POD)" }, { value: "bol", label: "Bill of lading (BOL)" }]} />
        <FileField ref={input} label="File from AmFast" accept="application/pdf,image/png,image/jpeg,image/tiff" onChange={(e) => setFile(e.target.files?.[0] ?? null)} error={err ?? undefined} hint="PDF, PNG, JPEG or TIFF up to 25 MB." />
      </div>
      <div className="ffx-actions">
        <Button
          loading={up.isPending}
          onClick={() => {
            const e = checkAmFastUpload(file);
            setErr(e);
            if (!e && file) up.mutate(file);
          }}
        >
          Attach
        </Button>
      </div>
    </div>
  );
}
